type EnvironmentName =
  | 'production'
  | 'staging'
  | 'acceptance'
  | 'development'
  | 'test';

const locales = [
  'en',
  'nl',
  'de',
  'pt',
  'es',
  'fr',
  'ru',
  'lv',
  'pl',
  'ro',
] as const;
const localesUnverified = ['lv', 'es', 'pt', 'ru', 'pl', 'ro'] as const;

interface Config {
  analyzeUrl?: string;
  backendUrl?: string;
  bookingWidgetUrl?: string;
  cloudinaryCloudName?: string;
  datadogApplicationId?: string;
  datadogClientToken: string;
  datadogSampleRate: number;
  datadogServiceName: string;
  datadogReplaySampleRate?: number;
  datadogTraceSampleRate?: number;
  env: EnvironmentName;
  gtmId?: string;
  gtmUrl?: string;
  helpCenterUrl: string;
  helpCenterUrlTreatwell: string;
  intercomId?: string;
  isDevelopment: boolean;
  isProduction: boolean;
  isStaging: boolean;
  isTest: boolean;
  isTreatwellDomain: boolean;
  locales: typeof locales;
  localesUnverified: typeof localesUnverified;
  productFruitsWorkspaceCode?: string;
  segmentWriteKey?: string;
  stripeKey?: string;
  unleashKey?: string;
  unleashUrl?: string;
  wsUrl?: string;
}

const getConfig = (env: EnvironmentName): Config => {
  const defaults = {
    bookingWidgetUrl: undefined,
    datadogClientToken: 'pub6a5e2675a57e48e959e8869d36c9961d',
    datadogSampleRate: 5,
    datadogServiceName: 'salonized',
    env,
    googleMapsApiKey: 'AIzaSyA4Cr1oIVxDMnVPLmFcwCQVvM84TTGwt1s',
    helpCenterUrl: 'https://help.salonized.com',
    helpCenterUrlTreatwell: 'https://support-expert.treatwell.com',
    isDevelopment: env === 'development',
    isProduction: env === 'production',
    isStaging: env === 'staging',
    isTest: env === 'test',
    isTreatwellDomain: window.location.hostname.endsWith('.treatwell.com'),
    locales,
    localesUnverified,
    segmentWriteKey: 'jDZ94W9gGPBsvjKppvpJMNzS3876fGQA',
  };

  const datadogApplicationId = 'fb8cb671-4648-4a33-95b4-018544dcc821';

  switch (env) {
    case 'production':
      return {
        ...defaults,
        // /analyze is only served on app.salonized.com, so brand domains
        // like expert.treatwell.com must be redirected there explicitly
        analyzeUrl: 'https://app.salonized.com',
        backendUrl: '',
        bookingWidgetUrl: 'https://widget.salonized.com',
        cloudinaryCloudName: 'salonized-eu-cld',
        datadogApplicationId,
        datadogReplaySampleRate: 0,
        // Backend traces that are automatically kept
        // Only keep a small portion, let DD decide the rest
        // using smart ingestion
        //
        // Because 99% of requests are on the calendar,
        // the ratio of traces is strongly biased towards the calendar
        datadogTraceSampleRate: 10,
        gtmId: 'GTM-59CF89',
        gtmUrl: 'https://analytics.salonized.com/gtm.js',
        intercomId: 'llecrrpz',
        productFruitsWorkspaceCode: 'nhaerdKAwa2Vby1A',
        segmentWriteKey: 'rGESuTvzO7bqRWmO9nB4GjxnWIPn4MoL',
        stripeKey: 'pk_live_7CLoj8Jn5PfrFEwKAm7UsIJJ',
        unleashKey:
          '*:production.e4779056c0eb8196a3dc19d460db12a37ee003dad3a68a0c2c247f1a',
        unleashUrl: 'https://unleash-edge.external.salonized.com/api/frontend',
        // salonized.com keeps the original cross-subdomain cable endpoint
        // (m.salonized.com has no same-origin /cable route); other domains
        // like expert.treatwell.com connect same-origin so the session
        // cookie is always sent
        wsUrl: window.location.hostname.endsWith('.salonized.com')
          ? 'wss://api.salonized.com/cable'
          : `${window.location.origin.replace(/^http/, 'ws')}/cable`,
      };
    case 'staging':
    case 'acceptance':
      return {
        ...defaults,
        backendUrl: '',
        bookingWidgetUrl: window.location.origin.replace('app', 'widget'),
        cloudinaryCloudName: 'salonized-testing',
        datadogApplicationId,
        datadogReplaySampleRate: 100,
        datadogTraceSampleRate: 100,
        gtmId: 'GTM-PL9MDMB',
        gtmUrl: 'https://analytics.salonized-staging.io/gtm.js',
        intercomId: 'f7f22yg8',
        productFruitsWorkspaceCode: 'vHIhZviK9I4zqpfc',
        stripeKey: 'pk_test_gSWRSjlQPoLS6jg2YsLiIMLn',
        unleashKey:
          '*:development.0f84e565a142704a354ea9263f77b180e77c5932f332fb9bdaaf4ce7',
        unleashUrl: 'https://unleash-edge.salonized-staging.io/api/frontend',
        wsUrl: `${window.location.origin.replace('http://app', 'ws://api')}/cable`,
      };
    case 'development':
      return {
        ...defaults,
        backendUrl: 'http://app.salonized.fml:3000',
        bookingWidgetUrl: 'http://localhost:8091',
        cloudinaryCloudName: 'salonized-testing',
        datadogReplaySampleRate: 100,
        datadogTraceSampleRate: 100,
        gtmId: '',
        gtmUrl: '',
        intercomId: 'f7f22yg8',
        productFruitsWorkspaceCode: 'vHIhZviK9I4zqpfc',
        stripeKey: 'pk_test_gSWRSjlQPoLS6jg2YsLiIMLn',
        unleashKey:
          '*:development.0f84e565a142704a354ea9263f77b180e77c5932f332fb9bdaaf4ce7',
        unleashUrl: 'https://unleash-edge.salonized-staging.io/api/frontend',
        wsUrl: 'ws://app.salonized.fml:28080/cable',
      };
    case 'test':
      return {
        ...defaults,
        stripeKey: 'test-stripe-key',
        backendUrl: 'http://localhost:8090',
        bookingWidgetUrl: 'http://someurl', // Add a url to prevent loading the current page in iframes,
        unleashUrl: 'http://unleash.salonized.fml:8080',
        unleashKey: 'test-key',
        productFruitsWorkspaceCode: 'test-workspace-code',
      };
    default:
      return {
        ...defaults,
      };
  }
};

export default Object.freeze(
  getConfig(window.Cypress ? 'test' : import.meta.env.VITE_ENV),
);
